<template>
    <div class="pa-4 fill-height d-flex flex-column flex-grow-1">
        <AgGrid class="flex-grow-1" ref="optionGridRef" :fetchUrl="optionFetchUrl" :columnDefs="optionColumnDefs"
        :gridParams="optionGridParams" :refresh="optionRefresh" :domLayout="domLayout"
        @manage-options="manageOptions" />
    </div>
</template>

<script setup>
    import { computed, inject, ref, watch } from 'vue';
    import AgGrid from '@/components/AgGrid.vue';
    import optionInfosJson from '@/components/json/optionInfos.json';
    import { toast } from 'vue3-toastify';
    import { useControlStore } from '@/stores/controlStore';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';

    const props = defineProps({
        customerReference: {
            type: String,
            default: null,
        },
    });
    const emit = defineEmits(['pending-changes']);

    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');

    const controlStore = useControlStore();
    const logStore = useLogStore();
    const userStore = useUserStore();

    const domLayout = 'normal';

    const optionFetchUrl = 'GetOptions';
    const optionColumnDefs = [
        { field: 'id', hide: true },
        { field: 'application', hide: true },
        { field: 'applicationName', rowGroup: true, hide: true },
        { field: 'name', headerName: translate('name', 'column'), sort: 'asc' },
        {
            field: 'value',
            headerName: translate('value', 'column'),
            editable: true,
            cellDataType: false,

            valueGetter: (params) => {
                if (typeof params.data == 'undefined')
                    return undefined;

                let type = getOptionValueType(params.data.value, params.data);

                if (type == 'boolean')
                    return ['true', 'yes'].indexOf(params.data.value.toString().toLowerCase()) > -1;

                return params.data.value;
            },

            valueSetter: (params) => {
                let type = getOptionValueType(params.data.value, params.data);

                params.data.value = (type == 'boolean') ? (params.newValue ? 'true' : 'false') : params.newValue;

                return true;
            },

            valueFormatter: (params) => {
                let type = getOptionValueType(params.value, params.data);

                switch (type) {
                    case 'size':
                        return params.value + ' Mo';

                    case 'date':
                        return new Date(params.value).getFullYear() > 1 ? new Date(params.value).toLocaleDateString() : '';

                    default:
                        return params.value;
                }
            },

            cellStyle: (params) => {
                if (getOptionValueType(params.value, params.data) == 'color')
                    return { backgroundColor: params.value };
            },

            cellEditorSelector: (params) => {
                switch (getOptionValueType(params.value, params.data)) {
                    case 'boolean':
                        return { component: 'agCheckboxCellEditor' };

                    case 'numeric':
                    case 'size':
                        return { component: 'agNumberCellEditor' };

                    case 'date':
                        return { component: 'agDateStringCellEditor' };

                    case 'list':
                        return { component: 'agSelectCellEditor', params: { values: optionListValues[params.data.name].map(v => v.id) } };

                    // custom cell renderer with vuetify colorpicker
                    case 'color':
                        return { component: 'colorPickerCellRenderer' };

                    // = 'string' => agTextCellEditor
                    default:
                        return undefined;
                }
            },
            cellRendererSelector: (params) => {
                if (getOptionValueType(params.value, params.data) == 'boolean')
                    return { component: 'agCheckboxCellRenderer' };

                // EVERYTHING ELSE USES THE DEFAULT CELL RENDERER + valueFormatter ABOVE
                return undefined;
            },
        },
        { field: 'description', headerName: translate('description', 'column') },
    ];
    const optionGridParams = {
        token: userStore.getUser().token,
        customerReference: props.customerReference,
    };

    const optionRefresh = ref(false);
    const optionListValues = {
        'SFA': [
            { id: 'Empty', name: 'Empty' },
            { id: 'Full', name: 'Full' }
        ],
    };
    const optionInfos = optionInfosJson;

    const rowsUpdated = ref([]);

    const hasPendingChanges = computed(() => rowsUpdated.value.length > 0);

    watch(hasPendingChanges, (value) => {
        emit('pending-changes', value);
    });

    // DETECTS THE "LOGICAL" TYPE OF AN OPTION VALUE SO THE VALUE COLUMN CAN PICK THE MATCHING
    // STANDARD AG GRID CELL EDITOR/RENDERER (boolean, numeric, size, date, list, color, string)
    function getOptionValueType(rawValue, data) {
        let optionListFields = ['SFA'];

        if (typeof data != 'undefined' && optionListFields.indexOf(data.name) > -1)
            return 'list';

        if (rawValue === null || typeof rawValue === 'undefined')
            return 'string';

        if (typeof rawValue === 'string' && rawValue.indexOf('#') > -1 && rawValue.length === 7)
            return 'color';

        if (rawValue.toString().indexOf('-') > -1 && rawValue.toString().length >= 10 && !Number.isNaN(new Date(rawValue).getTime()))
            return 'date';

        if (Number.isNaN(parseInt(rawValue))) {
            let lowerValue = rawValue.toString().toLowerCase();

            if (['true', 'false', 'yes', 'no'].indexOf(lowerValue) > -1)
                return 'boolean';

            return 'string';
        }

        if (typeof data != 'undefined' && typeof data.name === 'string' && data.name.toLowerCase().indexOf('size') > -1)
            return 'size';

        return 'numeric';
    }

    function manageOptions(rowData) {
        let rowToRemove = [];

        rowData.forEach((row) => {
            let optionInfo = optionInfos.filter(oi => oi.name == row.name);

            if (optionInfo != null && optionInfo.length > 0) {
                if (optionInfo.first().visible)
                    row['description'] = optionInfo.first().description;
                else
                    rowToRemove.push(row.name);
            }
        });

        controlStore.getDisplayedGrid().api.setGridOption('rowData', rowData.filter(r => rowToRemove.indexOf(r.name) == -1));
    }

    function undo() {
        var gridApi = controlStore.getDisplayedGrid().api;

        for (var i = 0; i < gridApi.getCurrentUndoSize() + 1; i++)
            setTimeout(() => {
                gridApi.undoCellEditing();
            }, 10 * i);

        gridApi.redrawRows();
        rowsUpdated.value = [];
    }

    function save() {
        rowsUpdated.value.forEach((row) => {
            row.value = row.value.toString();
        });

        let data = {
            token: userStore.getUser().token,
            customerReference: props.customerReference,
            options: rowsUpdated.value,
        };

        $.postData(urlWebAPI + 'SaveOptions', data)
            .then(() => {
                rowsUpdated.value = [];
                toast.success(translate('customerParamsWellSaved', 'toast'));
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing SaveOptions data.');
            });
    }

    // ViewManageCustomer.vue calls undo()/save() through a template ref, and AgGrid.vue reaches
    // into rowsUpdated through the $parent chain — both only see what defineExpose() exposes.
    defineExpose({ undo, save, rowsUpdated });
</script>
